import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics", { params: { days } }).then((r) => setData(r.data));
  }, [days]);

  if (!data) return <p className="text-neutral-400">Loading analytics…</p>;

  const maxDay = Math.max(1, ...data.by_day.map((d) => d.count));

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-neutral-400">Range: last <span className="text-gold font-bold">{days}</span> days · <span className="text-gold font-bold">{data.total}</span> page views</p>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`text-xs font-bold uppercase tracking-widest px-3 py-2 border transition ${days === d ? "bg-svn-red text-white border-svn-red" : "border-white/15 text-neutral-300 hover:border-white/40"}`}
              data-testid={`analytics-range-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-section-views">
        {[
          ["Sponsors page", data.section_views.sponsors],
          ["Membership", data.section_views.membership],
          ["Contact", data.section_views.contact],
          ["SVN Español", data.section_views.espanol],
        ].map(([k, v]) => (
          <div key={k} className="bg-[#0F0F0F] border border-white/5 p-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-500">{k}</p>
            <p className="mt-2 font-display text-3xl font-black text-gold">{v}</p>
          </div>
        ))}
      </div>

      <div className="bg-[#0F0F0F] border border-white/10 p-6" data-testid="analytics-by-day">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-4">Views by day</p>
        {data.by_day.length === 0 ? (
          <p className="text-sm text-neutral-500">No views yet in this range.</p>
        ) : (
          <div className="flex items-end gap-1 h-32">
            {data.by_day.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.day}: ${d.count}`}>
                <div
                  className="w-full bg-svn-red group-hover:bg-gold transition-colors"
                  style={{ height: `${Math.max(2, (d.count / maxDay) * 100)}%` }}
                />
                <span className="text-[9px] text-neutral-500 -rotate-45 origin-top-left translate-x-1 truncate w-8">{d.day.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#0F0F0F] border border-white/10 p-6" data-testid="analytics-top-pages">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-4">Top pages</p>
          {data.by_path.length === 0 ? (
            <p className="text-sm text-neutral-500">No data yet.</p>
          ) : (
            <ul className="space-y-2">
              {data.by_path.map((row) => (
                <li key={row.path} className="flex items-center gap-3 text-sm">
                  <span className="text-neutral-300 font-mono truncate flex-1">{row.path}</span>
                  <span className="text-gold font-bold">{row.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-[#0F0F0F] border border-white/10 p-6" data-testid="analytics-top-episodes">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-4">Most-viewed episodes</p>
          {data.top_episodes.length === 0 ? (
            <p className="text-sm text-neutral-500">No episode views tracked yet. Episode views are tracked when a visitor opens an episode page (future enhancement).</p>
          ) : (
            <ul className="space-y-2">
              {data.top_episodes.map((row) => (
                <li key={row.episode_id} className="flex items-center gap-3 text-sm">
                  <span className="text-neutral-300 truncate flex-1">{row.title || row.episode_id}</span>
                  <span className="text-gold font-bold">{row.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
